import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { BehaviorSubject, take } from 'rxjs';
import {
  LucideAngularModule,
  Presentation,
  LayoutList,
  Share2,
  ClipboardList,
  FileText,
  MessageSquareHeart,
  LayoutDashboard,
  GraduationCap,
} from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import {
  THEME_COLOR,
  LessonSlide,
  GreetingSlideContent,
  TextSlideContent,
  ListSlideContent,
} from '../../services/lesson-plan.models';
import { ExportModalComponent } from '../../components/export-modal/export-modal';
import { FeedbackModalComponent } from '../../../../../common/components/feedback-modal/feedback-modal';

@Component({
  selector: 'app-step-content',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ExportModalComponent,
    FeedbackModalComponent,
    RouterLink,
    RouterLinkActive,
  ],
  templateUrl: './step-content.html',
  styleUrl: './step-content.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepContentComponent implements OnInit {
  private readonly lessonPlanService = inject(LessonPlanService);

  @Output() readonly back = new EventEmitter<void>();

  protected readonly themeColor = THEME_COLOR;
  protected readonly slides$ = this.lessonPlanService.slides$;
  protected readonly planStructure$ = this.lessonPlanService.planStructure$;
  protected readonly inputData$ = this.lessonPlanService.inputData$;
  protected readonly lessonPlanId$ = this.lessonPlanService.lessonPlanId$;

  // Slides are now pre-generated before navigating to this step
  private readonly loading$$ = new BehaviorSubject<boolean>(false);
  protected readonly loading$ = this.loading$$.asObservable();

  protected readonly isExportModalOpen = signal<boolean>(false);
  protected readonly isFeedbackModalOpen = signal<boolean>(false);

  protected readonly icons = {
    Presentation,
    LayoutList,
    Share2,
    ClipboardList,
    FileText,
    MessageSquareHeart,
    LayoutDashboard,
    GraduationCap,
  };

  ngOnInit(): void {
    // Slides are now pre-generated via API before navigating to this step
    // Just check if slides exist for safety
    this.slides$.pipe(take(1)).subscribe((slides) => {
      if (slides.length === 0) {
        console.warn('No slides available - this should not happen as slides are generated before navigation');
      }
    });
  }

  protected openExportModal(): void {
    this.isExportModalOpen.set(true);
  }

  protected closeExportModal(): void {
    this.isExportModalOpen.set(false);
  }

  protected openFeedbackModal(): void {
    this.isFeedbackModalOpen.set(true);
  }

  protected closeFeedbackModal(): void {
    this.isFeedbackModalOpen.set(false);
  }

  /**
   * Get display title for a slide based on its type and content
   */
  protected getSlideTitle(slide: LessonSlide): string {
    switch (slide.type) {
      case 'greeting': {
        const content = slide.content as GreetingSlideContent;
        return content.title || 'Opening';
      }
      case 'text': {
        const content = slide.content as TextSlideContent;
        return content.title || 'Concept';
      }
      case 'list': {
        const content = slide.content as ListSlideContent;
        return content.title || 'Key Points';
      }
      case 'bye': {
        return 'Closing';
      }
      default:
        return 'Slide';
    }
  }
}
